import React from 'react';
import styled from 'styled-components';
import { GameProvider } from './contexts/GameContext';
import { Web3Provider } from './contexts/Web3Context';
import WalletConnect from './components/WalletConnect';
import TariffQuestion from './components/TariffQuestion';
import ScoreDisplay from './components/ScoreDisplay';
import GameOver from './components/GameOver';
import FeedbackAnimation from './components/FeedbackAnimation';
import XiEasterEgg from './components/XiEasterEgg';
import ProfileDropdown from './components/ProfileDropdown';
import { useGame } from './contexts/GameContext';
import SolanaGlobalStyles from './styles/SolanaTheme';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';
import '@solana/wallet-adapter-react-ui/styles.css';

const AppContainer = styled.div`
  font-family: 'Poppins', sans-serif;
  max-width: 1000px;
  margin: 0 auto;
  padding: 30px 20px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const AppBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at top right, #232526, #141414);
  z-index: -1;
  
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239945FF' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    opacity: 0.3;
  }
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 40px;
  background: rgba(30, 30, 40, 0.7);
  backdrop-filter: blur(10px);
  padding: 25px;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  position: relative;
  overflow: visible;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const Title = styled.h1`
  font-size: 48px;
  background: linear-gradient(90deg, #9945FF, #14F195);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 15px;
  font-weight: 800;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  letter-spacing: -0.5px;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: var(--text-secondary);
  margin-bottom: 25px;
  font-weight: 500;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ConnectPrompt = styled.div`
  text-align: center;
  background: rgba(30, 30, 40, 0.7);
  backdrop-filter: blur(10px);
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  max-width: 500px;
  margin: 0 auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  h2 {
    background: linear-gradient(90deg, #9945FF, #14F195);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    font-size: 32px;
    margin-bottom: 20px;
    font-weight: 700;
  }
  
  p {
    color: rgba(255, 255, 255, 0.8);
    font-size: 18px;
    margin-bottom: 30px;
    line-height: 1.6;
  }
`;

const Footer = styled.footer`
  text-align: center;
  margin-top: 40px;
  padding: 20px 0;
  color: var(--text-secondary);
  font-size: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  
  div {
    color: rgba(255, 255, 255, 0.6);
  }
`;

const SocialLink = styled.a`
  color: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    color: #14F195;
    transform: translateY(-2px);
    text-decoration: none;
  }
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const SolanaLogo = styled.img`
  height: 30px;
  width: auto;
  margin-bottom: 15px;
`;

const GameContent = () => {
  const { isGameOver, walletConnected } = useGame();

  if (!walletConnected) {
    return (
      <ConnectPrompt>
        <h2>Welcome to Tariff Game!</h2>
        <p>Connect your Solana wallet to start making tariff decisions and earn tariff score points. Can you reach the maximum score?</p>
        <WalletConnect />
      </ConnectPrompt>
    );
  }

  return (
    <ContentWrapper>
      <ScoreDisplay />
      {isGameOver ? <GameOver /> : <TariffQuestion />}
      <FeedbackAnimation />
    </ContentWrapper>
  );
};

function App() {
  return (
    <GameProvider>
      <Web3Provider>
        <WalletModalProvider>
          <SolanaGlobalStyles />
          <AppBackground />
          <AppContainer>
            <Header>
              <ProfileDropdown />
              <Title>Tariff Score</Title>
              <Subtitle>Make strategic tariff decisions, earn tariff points!</Subtitle>
            </Header>
            <GameContent />
            <Footer>
              <SocialLink href="https://x.com/tariffscore" target="_blank" rel="noopener noreferrer">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </SocialLink>
              <div>&copy; {new Date().getFullYear()} Tariff Game | All rights reserved</div>
            </Footer>
            <XiEasterEgg />
          </AppContainer>
        </WalletModalProvider>
      </Web3Provider>
    </GameProvider>
  );
}

export default App;
